import React, { useMemo } from 'react';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useLoadLineVisibility, useLoadLineThickness } from '@/stores/appStore';
import type { LightingFixture, ControlType } from '@/types/floorPlanViewer';

interface Point {
  x: number;
  y: number;
}

/** Get the representative position of a fixture for load line routing */
function getFixturePosition(fixture: LightingFixture): Point {
  if (fixture.fixture_form_factor === 'linear' && fixture.strip_points?.length) {
    // Use midpoint of the strip
    const points = fixture.strip_points;
    const mid = Math.floor(points.length / 2);
    return points[mid];
  }
  return { x: fixture.x_position, y: fixture.y_position };
}

/** Euclidean distance between two points */
function distance(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Nearest-neighbor daisy chain ordering.
 * Starts from the first fixture and greedily visits the nearest unvisited fixture.
 */
function nearestNeighborOrder(fixtures: LightingFixture[]): LightingFixture[] {
  if (fixtures.length <= 1) return fixtures;

  const remaining = [...fixtures];
  const ordered: LightingFixture[] = [remaining.shift()!];

  while (remaining.length > 0) {
    const lastPos = getFixturePosition(ordered[ordered.length - 1]);
    let nearestIdx = 0;
    let nearestDist = Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const d = distance(lastPos, getFixturePosition(remaining[i]));
      if (d < nearestDist) {
        nearestDist = d;
        nearestIdx = i;
      }
    }

    ordered.push(remaining.splice(nearestIdx, 1)[0]);
  }

  return ordered;
}

/**
 * Generate a curved arc SVG path between two points.
 * Uses a quadratic bezier with control point offset perpendicular to the line.
 */
function curvedArcPath(from: Point, to: Point): string {
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.sqrt(dx * dx + dy * dy);

  // Arc bulge: 15% of distance, perpendicular to the line
  const bulge = len * 0.15;
  // Perpendicular direction (rotate 90 degrees)
  const px = -dy / len;
  const py = dx / len;

  const cx = midX + px * bulge;
  const cy = midY + py * bulge;

  return `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`;
}

interface LoadConnectionLayerProps {
  scale: number;
}

export function LoadConnectionLayer({ scale }: LoadConnectionLayerProps) {
  const { data } = useFloorPlanContext();
  const isVisible = useLoadLineVisibility();
  const thickness = useLoadLineThickness();

  const fixtures = data.fixtures || [];
  const controls = data.controls || [];
  const circuits = data.circuits || [];

  // Group fixtures by circuit_id
  const fixturesByCircuit = useMemo(() => {
    const map: Record<string, LightingFixture[]> = {};
    for (const f of fixtures) {
      if (f.circuit_id) {
        if (!map[f.circuit_id]) map[f.circuit_id] = [];
        map[f.circuit_id].push(f);
      }
    }
    return map;
  }, [fixtures]);

  // Map controls by circuit_id (only first control per circuit)
  const controlByCircuit = useMemo(() => {
    const map: Record<string, ControlType> = {};
    for (const c of controls) {
      if (c.circuit_id && !map[c.circuit_id]) {
        map[c.circuit_id] = c;
      }
    }
    return map;
  }, [controls]);

  // Compute all load connection paths
  const loadPaths = useMemo(() => {
    if (!isVisible) return [];

    const paths: Array<{ id: string; d: string }> = [];

    for (const circuit of circuits) {
      const circuitFixtures = fixturesByCircuit[circuit.id];
      if (!circuitFixtures || circuitFixtures.length < 2) {
        // Still check for control connection with a single fixture
        if (circuitFixtures?.length === 1) {
          const control = controlByCircuit[circuit.id];
          if (control) {
            const fixturePos = getFixturePosition(circuitFixtures[0]);
            const controlPos: Point = { x: control.x, y: control.y };
            paths.push({
              id: `load-ctrl-${circuit.id}`,
              d: curvedArcPath(fixturePos, controlPos),
            });
          }
        }
        continue;
      }

      // Order fixtures by nearest-neighbor
      const ordered = nearestNeighborOrder(circuitFixtures);

      // Draw arcs between consecutive fixtures in the chain
      for (let i = 0; i < ordered.length - 1; i++) {
        const from = getFixturePosition(ordered[i]);
        const to = getFixturePosition(ordered[i + 1]);
        paths.push({
          id: `load-${circuit.id}-${i}`,
          d: curvedArcPath(from, to),
        });
      }

      // Draw arc from nearest fixture to control (if assigned)
      const control = controlByCircuit[circuit.id];
      if (control) {
        const controlPos: Point = { x: control.x, y: control.y };
        // Find nearest fixture in chain to the control
        let nearestFixture = ordered[0];
        let nearestDist = Infinity;
        for (const f of ordered) {
          const d = distance(getFixturePosition(f), controlPos);
          if (d < nearestDist) {
            nearestDist = d;
            nearestFixture = f;
          }
        }
        paths.push({
          id: `load-ctrl-${circuit.id}`,
          d: curvedArcPath(getFixturePosition(nearestFixture), controlPos),
        });
      }
    }

    return paths;
  }, [isVisible, circuits, fixturesByCircuit, controlByCircuit]);

  if (!isVisible || loadPaths.length === 0) return null;

  return (
    <g className="load-connection-layer" style={{ pointerEvents: 'none' }}>
      {loadPaths.map(path => (
        <path
          key={path.id}
          d={path.d}
          fill="none"
          stroke="#9CA3AF"
          strokeWidth={thickness}
          strokeDasharray="8 6"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ pointerEvents: 'none' }}
        />
      ))}
    </g>
  );
}
